import { Side } from '@/compiled_proto/com/celertech/marketdata/api/enums/SideProto';
import HighlightedPips from '@/components/common/HighlightedPips';
import { TickerOrderFormInput } from '@/components/form/schema/tickerOrderSchema';
import { numberFormatterConfig, tickerIdleTimer } from '@/config/config';
import { accumulatePriceBookBidAsks } from '@/helpers/orderbookHelper';
import { useAppSelector } from '@/state/hooks';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { selectTradingSettings } from '@/state/reducers/settingSlice';
import { formatTraderNumber } from '@/utils/format';
import { useDebouncedOneClickTrading } from '@/utils/hooks/useDebouncedOneClickTrading';
import { UseDisclosureReturn } from '@/utils/hooks/useDisclosure';
import { GetColorIndexesReturn, GetPipIndexesReturn, useInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useTickerIdle } from '@/utils/hooks/useTickerIdle';
import cn from 'classnames';
import { Fragment, cloneElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import { useFormContext } from 'react-hook-form';

interface TickerPriceProps {
    side: Side;
    market: PairMap;
    bestPrice: number;
    currencyAssetName: string;
    showOrderBook: UseDisclosureReturn;
    alignItems?: 'left' | 'right';
    priceType: 'bid' | 'ask';
    isCcy2Order: boolean;
    onSubmit?: () => void;
    children: React.ReactNode;
    disabled?: boolean;
}

const expirationDuration = 5000;

const TickerPrice = (props: TickerPriceProps) => {
    const {
        side,
        bestPrice,
        showOrderBook,
        currencyAssetName,
        alignItems = 'right',
        disabled = false,
        onSubmit,
        children
    } = props;

    const trading = useAppSelector(selectTradingSettings);

    const isTwoClickTrading = useMemo(() => trading?.twoClickTrading?.value || false, [trading]);

    const [locked, setLocked] = useState<boolean>(!!isTwoClickTrading);
    const [expirationTimer, setExpirationTimer] = useState<number>(expirationDuration);

    const onDebouncedOneClick = useDebouncedOneClickTrading(setLocked, 300);

    const { setValue } = useFormContext<TickerOrderFormInput>();

    const tickerTitle = useMemo(() => {
        if (side === Side.BUY) return `BUY ${currencyAssetName}`;
        else return `SELL ${currencyAssetName}`;
    }, [currencyAssetName, side]);

    // is not disabled and it has a price
    const isTradeable = useMemo(() => !disabled && !!bestPrice, [disabled, bestPrice, locked]);

    const onOneClickHandler = useCallback(
        (event) => {
            onDebouncedOneClick(false);
            if (!locked) {
                setValue('side', side);
                setValue('bestPrice', bestPrice);
                onSubmit?.();
                setLocked(true);
            } else {
                event.preventDefault();
            }
        },
        [locked, bestPrice, side]
    );

    const onTwoClickHandler = useCallback(
        (event) => {
            if (locked && isTradeable) {
                setLocked(false);
                setExpirationTimer(5000);
                event.preventDefault();
            } else {
                setValue('side', side);
                setValue('bestPrice', bestPrice);
                onSubmit?.();
                setLocked(true);
            }
        },
        [locked, isTradeable, bestPrice, side]
    );

    useEffect(() => {
        setLocked(!!isTwoClickTrading);
    }, [isTwoClickTrading]);

    useEffect(() => {
        if (!locked && isTwoClickTrading) {
            const timer = setTimeout(() => setExpirationTimer(expirationTimer - 1000), 1000);
            if (expirationTimer <= 0) {
                clearTimeout(timer);
                setLocked(true);
            }
        }
    }, [locked, isTwoClickTrading, expirationTimer]);

    return (
        <div className="flex-1 basis-0">
            <div
                className={cn('relative h-12 w-full flex items-center cursor-pointer p-1 pt-3.5 z-[1] select-none', {
                    ['border-2 border-brand-green hover:border-brand-green-light']: side === Side.BUY,
                    ['border-2 border-brand-red hover:border-brand-red-light']: side === Side.SELL,
                    [`bg-brand-green hover:bg-brand-green-light`]: isTradeable && !locked && side === Side.BUY,
                    [`bg-brand-red hover:bg-brand-red-light`]: isTradeable && !locked && side === Side.SELL,
                    ['pl-1 justify-start']: alignItems === 'left',
                    ['pr-1 justify-end']: alignItems === 'right',
                    'cursor-not-allowed': !isTradeable
                })}
                onClick={(e) => {
                    if (isTradeable) {
                        if (!isTwoClickTrading) onOneClickHandler(e);
                        else onTwoClickHandler(e);
                    }
                }}>
                {cloneElement(children as React.ReactElement, { locked })}
                <div
                    className={cn('absolute top-0 uppercase text-2xs', {
                        ['left-1']: alignItems === 'left',
                        ['right-1']: alignItems === 'right'
                    })}>
                    {tickerTitle}
                </div>
            </div>
            {showOrderBook[0] && <TickerVerticalBook {...props} />}
        </div>
    );
};

export default TickerPrice;

interface HighlightsProps {
    ticker?: GetPipIndexesReturn;
    highlights?: GetColorIndexesReturn;
    isNoPrice?: boolean;
    locked?: boolean;
}

export const Highlights = ({ ticker, locked, highlights, isNoPrice }: HighlightsProps) => {
    const lang = useLanguage();
    const tickerIdle = useTickerIdle(tickerIdleTimer, highlights);
    return (
        <div className="mt-1.5">
            {ticker && !isNoPrice ? (
                <HighlightedPips
                    price={ticker.price}
                    boldIndexes={ticker.pipIndexes}
                    colorIndexes={!tickerIdle && locked ? highlights?.colorIndexes : []}
                    color={!tickerIdle && locked ? highlights?.color : 'text-brand-text'}
                    fontSize="text-lg"
                />
            ) : (
                <span className="text-sm">{lang.tickers.noPrice}</span>
            )}
        </div>
    );
};

const TickerVerticalBook = ({ market, side, priceType, alignItems, isCcy2Order }: TickerPriceProps) => {
    const priceBooks = useAppSelector((state) => state.celerMarket.priceBooks[market.celer]);
    const { bids, asks } = useMemo(() => accumulatePriceBookBidAsks(priceBooks), [priceBooks, market.celer]);

    const lang = useLanguage();
    const { formatPip } = useInstrument(market.celer);

    const hasOrderBook = bids && asks && bids[0] && asks[0];

    const { rows, ...rowProps } = useMemo(() => {
        let rows: any = [];
        if (priceType === 'ask' && asks) {
            const asksCopy = [...asks];
            rows = asksCopy.reverse();
        } else {
            rows = bids;
        }

        const type: 'ask' | 'bid' = priceType;

        if (rows) {
            return {
                rows,
                side,
                type,
                formatPip,
                totalQuantity: rows[0]?.accQuantity || 0,
                alignItems,
                isCcy2Order
            };
        } else {
            return {
                rows: [],
                side,
                type,
                formatPip,
                totalQuantity: 0,
                alignItems,
                isCcy2Order
            };
        }
    }, [bids, asks, side, alignItems, formatPip]);

    return (
        <div
            className={cn(
                'ticker-order-book h-32 lg:w-[113px] border border-t-0 border-brand-neutral-600 overflow-y-auto',
                {
                    sell: side === Side.SELL
                }
            )}>
            <Fragment>
                {!hasOrderBook ? (
                    <div className="p-2 text-xs italic text-brand-neutral-500">{lang.tickers.noOrderBookData}</div>
                ) : (
                    <div className="divide-y divide-brand-neutral-600">
                        {rows.map((row, i) => (
                            <OrderBookRow key={i} {...row} {...rowProps} />
                        ))}
                    </div>
                )}
            </Fragment>
        </div>
    );
};

interface OrderBookRowProps {
    side: Side;
    price: number;
    quantity: number;
    accQuantity: number;
    totalQuantity: number;
    formatPip: (price: number) => GetPipIndexesReturn;
    type: 'ask' | 'bid';
    alignItems?: 'left' | 'right';
    isCcy2Order: boolean;
}

const OrderBookRow = ({ side, price, quantity, formatPip, alignItems }: OrderBookRowProps) => {
    const defaultFormatNumber = useNumberFormatter({ ...numberFormatterConfig, minimumFractionDigits: 0 });
    const thousandsFormatNumber = useNumberFormatter({ maximumFractionDigits: 0, notation: 'compact' });
    const millionsFormatNumber = useNumberFormatter({ maximumFractionDigits: 1, notation: 'compact' });

    const { price: formattedPrice, pipIndexes } = formatPip(price);

    return (
        <div
            className={cn(
                'w-full justify-between flex gap-1 text-xs px-1 py-1 relative group hover:bg-brand-background-dark',
                {
                    'flex-row-reverse': alignItems === 'right'
                }
            )}>
            <div className="flex items-center text-neutral-500 overflow-hidden">
                <span className="truncate">
                    {formatTraderNumber(quantity, defaultFormatNumber, thousandsFormatNumber, millionsFormatNumber)}
                </span>
            </div>
            <div
                className={cn('', {
                    'text-brand-red': side === Side.SELL,
                    'text-brand-green': side === Side.BUY
                })}>
                <HighlightedPips price={formattedPrice} boldIndexes={pipIndexes} fontSize="text-base" />
            </div>
        </div>
    );
};
